import { useState } from 'react';
import { NJButton, NJFormItem, NJInlineMessage, NJText, NJToggle } from '@engie-group/fluid-design-system-react';
import { inputValue, toNumberOrNull, RightSidePanel } from '../ref-production-fuels/ui';
import { POWER_CONTRACT_NAME_MAX } from './fixtures';
import type { PowerContract, PowerContractInput } from './types';

const text = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));
const isBad = (value: string) => { const n = toNumberOrNull(value); return n !== null && (Number.isNaN(n) || n < 0); };

/** Nemo `PowerContractCreateModal` / `PowerContractEditModal` (`RightSideModal`, title = contract name in edit mode). */
export default function PowerContractPanel({ contract, existingNames, onClose, onSubmit, error, isPending }: {
    contract?: PowerContract;
    existingNames: string[];
    onClose: () => void;
    onSubmit: (value: PowerContractInput) => void;
    error?: string;
    isPending?: boolean;
}) {
    const [name, setName] = useState(contract?.name ?? '');
    const [startDate, setStartDate] = useState(contract?.startDate ?? '');
    const [endDate, setEndDate] = useState(contract?.endDate ?? '');
    const [fixedPrice, setFixedPrice] = useState(text(contract?.fixedPrice));
    const [description, setDescription] = useState(contract?.description ?? '');
    const [advanced, setAdvanced] = useState(false);
    const [maxPower, setMaxPower] = useState(text(contract?.defaultMaxPower));
    const [overrunPrice, setOverrunPrice] = useState(text(contract?.defaultOverrunPrice));

    const trimmed = name.trim();
    const duplicate = existingNames.some((existing) => existing.toLowerCase() === trimmed.toLowerCase() && existing !== contract?.name);
    const datesInvalid = !!startDate && !!endDate && startDate > endDate;
    const fixedNumber = toNumberOrNull(fixedPrice);
    const canSubmit = !!trimmed && !duplicate && !!startDate && !!endDate && !datesInvalid && fixedNumber !== null && !isBad(fixedPrice) && !isBad(maxPower) && !isBad(overrunPrice) && !isPending;

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            name: trimmed,
            startDate,
            endDate,
            fixedPrice: fixedNumber,
            description: description.trim(),
            defaultMaxPower: toNumberOrNull(maxPower),
            defaultOverrunPrice: toNumberOrNull(overrunPrice),
        });
    };

    return (
        <RightSidePanel
            isOpen
            onClose={onClose}
            title={contract ? contract.name : "Créer un nouveau contrat d'électricité"}
            testId="power-contract-panel"
            footer={
                <>
                    <NJButton label="Annuler" variant="secondary" onClick={onClose} />
                    <NJButton label={contract ? 'Valider' : 'Créer'} type="submit" form="power-contract-form" disabled={!canSubmit} />
                </>
            }
        >
            <form id="power-contract-form" className="production-contracts-form production-contracts-panel-form" onSubmit={submit} noValidate>
                {error && <NJInlineMessage variant="error">{error}</NJInlineMessage>}
                <NJText scale="sm" variant="secondary">Tous les champs hors options avancées sont obligatoires</NJText>
                <NJFormItem
                    id="power-contract-name"
                    label="Nom"
                    isRequired
                    value={name}
                    maxLength={POWER_CONTRACT_NAME_MAX}
                    onChange={(event) => setName(inputValue(event))}
                    hasError={duplicate}
                    subscriptMessage={duplicate ? 'Un contrat avec ce nom existe déjà.' : `${name.length}/${POWER_CONTRACT_NAME_MAX} caractères`}
                />
                <div className="production-contracts-grid">
                    <NJFormItem id="power-contract-start" label="Date de début" isRequired type="date" value={startDate} onChange={(event) => setStartDate(inputValue(event))} />
                    <NJFormItem
                        id="power-contract-end"
                        label="Date de fin"
                        isRequired
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(inputValue(event))}
                        hasError={datesInvalid}
                        subscriptMessage={datesInvalid ? 'La date de fin doit être postérieure à la date de début.' : undefined}
                    />
                </div>
                <NJFormItem id="power-contract-fixed-price" label="Prix fixe (€/J)" isRequired type="number" value={fixedPrice} onChange={(event) => setFixedPrice(inputValue(event))} hasError={isBad(fixedPrice)} />
                <NJFormItem id="power-contract-description" label="Description" isMultiline rows={3} value={description} onChange={(event) => setDescription(inputValue(event))} />
                <NJToggle id="power-contract-advanced" label="Afficher les options avancées" checked={advanced} onChange={(_event, checked) => setAdvanced(checked)} />
                {advanced && (
                    <>
                        <NJFormItem id="power-contract-max-power" label="Puissance max par défaut (W)" type="number" value={maxPower} onChange={(event) => setMaxPower(inputValue(event))} hasError={isBad(maxPower)} />
                        <NJFormItem id="power-contract-overrun" label="Prix de dépassement par défaut (€/J)" type="number" value={overrunPrice} onChange={(event) => setOverrunPrice(inputValue(event))} hasError={isBad(overrunPrice)} />
                    </>
                )}
            </form>
        </RightSidePanel>
    );
}
